import React, { useEffect, useState } from 'react';
import { ArrowRight, BookOpen, Check, HelpCircle, Repeat, Zap } from 'lucide-react';
import T from 'Components/Helpers/translate';
import AssistantRoutinesResource, { type Routine } from 'Resources/AssistantRoutines';

type CardId = 'automate' | 'doNow' | 'ask' | 'catchUp';

type LatestTone = 'ok' | 'mute' | 'warn';

type LatestPreview = {
	title: string;
	status: string;
	tone: LatestTone;
};

type CardSpec = {
	id: CardId;
	Icon: typeof Repeat;
	tint: string;
};

const CARDS: ReadonlyArray<CardSpec> = [
	{ id: 'automate', Icon: Repeat, tint: 'var(--ws-a-prospecting, #5F7F6A)' },
	{ id: 'doNow', Icon: Zap, tint: 'var(--ws-a-alex, #C47A4A)' },
	{ id: 'ask', Icon: HelpCircle, tint: 'var(--ws-a-forecasting, #4A6B85)' },
	{ id: 'catchUp', Icon: BookOpen, tint: 'var(--ws-a-postmeeting, #8B5A8F)' }
];

const KEY = (id: CardId, leaf: string): string => `assistant.home.${id}.${leaf}`;

const truncate = (s: string, max = 56): string => (s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s);

const sortRoutinesRecent = (rs: Routine[]): Routine[] =>
	[...rs].sort((a, b) => {
		const ad = a.lastRun || a.lastFired || a.modDate || a.regDate || '';
		const bd = b.lastRun || b.lastFired || b.modDate || b.regDate || '';
		return bd.localeCompare(ad);
	});

type Props = {
	onSendPrompt: (text: string) => void;
};

const WorkspaceHomeCards = ({ onSendPrompt }: Props) => {
	const [routine, setRoutine] = useState<Routine | null>(null);

	useEffect(() => {
		let cancelled = false;
		AssistantRoutinesResource.getRoutines()
			.then(rs => {
				if (cancelled) return;
				setRoutine(sortRoutinesRecent(rs)[0] ?? null);
			})
			.catch(() => {
				/* Home card preview — silent on failure. */
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const latestFor = (id: CardId): LatestPreview | null => {
		if (id === 'automate' && routine) {
			const label = routine.label || routine.prompt || '';
			if (!label) return null;
			return {
				title: truncate(label),
				status: routine.enabled ? T('assistant.home.statusActive') : T('assistant.home.statusPaused'),
				tone: routine.enabled ? 'ok' : 'mute'
			};
		}
		return null;
	};

	return (
		<div className="ws-home-cards">
			{CARDS.map(({ id, Icon, tint }) => {
				const latest = latestFor(id);
				const handleClick = () => onSendPrompt(T(KEY(id, 'prompt')));
				return (
					<button
						key={id}
						type="button"
						className="ws-home-card"
						onClick={handleClick}
						style={{ ['--ws-home-tint' as string]: tint }}
					>
						<div className="ws-home-card-head">
							<span className="ws-home-card-icon" aria-hidden="true">
								<Icon size={18} strokeWidth={1.75} />
							</span>
							<div className="ws-home-card-titles">
								<span className="ws-home-card-title">{T(KEY(id, 'title'))}</span>
								<span className="ws-home-card-tagline">{T(KEY(id, 'tagline'))}</span>
							</div>
						</div>
						<p className="ws-home-card-body">{T(KEY(id, 'body'))}</p>
						<ul className="ws-home-card-examples">
							{[1, 2].map(n => (
								<li key={n}>
									<Check size={11} strokeWidth={2.25} aria-hidden="true" />
									<span>{T(KEY(id, `ex${n}`))}</span>
								</li>
							))}
						</ul>
						{latest && (
							<div className="ws-home-card-latest">
								<span className="ws-home-card-latest-label">{T('assistant.home.latest')}</span>
								<span className="ws-home-card-latest-row">
									<span
										className={`ws-home-card-latest-dot ws-home-card-latest-${latest.tone}`}
										aria-hidden="true"
									/>
									<span className="ws-home-card-latest-title">{latest.title}</span>
									<span className={`ws-home-card-latest-status ws-home-card-latest-${latest.tone}`}>
										{latest.status}
									</span>
								</span>
							</div>
						)}
						<span className="ws-home-card-cta">
							<span>{T(KEY(id, 'cta'))}</span>
							<ArrowRight size={14} strokeWidth={2} />
						</span>
					</button>
				);
			})}
		</div>
	);
};

export default WorkspaceHomeCards;
